import { getMigrationHistory } from "@domain/migrations"
import useTranslate from "@hooks/use_translate"
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import {
  INDEX_ACTION_COLOR,
  MigrationRow,
  MigrationRun,
  MigrationState,
  RISK_COLOR,
  RUN_STATUS_COLOR,
  STATUS_COLOR,
  formatElapsed,
  hasOutstandingWork,
  isSeedSpec,
  pickText
} from "./migration.models"
import SampleDiff from "./sample_diff"

type Props = {
  state: MigrationState
  lastRun?: MigrationRun
  /** Another migration holding this environment's single run slot. */
  otherRun?: MigrationRow
  now: number
  onClose: () => void
  onRun: (state: MigrationState) => void
  onReprobe: () => void
  reprobing: boolean
}

const mono = {
  fontFamily: "monospace",
  fontSize: 12,
  whiteSpace: "pre-wrap" as const
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="overline" color="text.secondary">
      {children}
    </Typography>
  )
}

export default function MigrationDetailDrawer({
  state,
  lastRun,
  otherRun,
  now,
  onClose,
  onRun,
  onReprobe,
  reprobing
}: Props) {
  const { translate, currentLanguage } = useTranslate()
  const { data: history, isFetching: historyLoading } = useQuery({
    queryKey: ["migration-history", state.spec.type],
    queryFn: () => getMigrationHistory(state.spec.type)
  })

  const { spec } = state
  const runs = history?.data?.data ?? []
  const actionable = hasOutstandingWork(state)
  const running = lastRun?.status === "RUNNING"

  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", md: 760 }, p: 3 } }}
    >
      <Stack spacing={2.5}>
        <Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            <Typography variant="h6">
              {pickText(spec.title, currentLanguage)}
            </Typography>
            <Chip
              size="small"
              label={translate(`migration_status_${state.status}`)}
              color={STATUS_COLOR[state.status]}
            />
            <Chip
              size="small"
              variant="outlined"
              label={translate(`migration_risk_${spec.risk}`)}
              color={RISK_COLOR[spec.risk]}
            />
          </Stack>
          <Typography variant="caption" sx={mono} color="text.secondary">
            {spec.type} · {spec.collection}
          </Typography>
        </Box>

        <Alert severity={actionable ? "warning" : "info"}>
          <AlertTitle>
            {translate(`migration_status_${state.status}`)}
          </AlertTitle>
          {translate(`migration_status_help_${state.status}`)}
        </Alert>

        {state.probeError && (
          <Alert severity="error">
            {translate("migration_warning_PROBE_ERROR", {
              message: state.probeError
            })}
          </Alert>
        )}

        {(spec.notes ?? []).map((note) => (
          <Alert key={note.en} severity="warning">
            {pickText(note, currentLanguage)}
          </Alert>
        ))}

        <Box>
          <SectionTitle>{translate("migration_section_what")}</SectionTitle>
          <Typography variant="body2">
            {pickText(spec.description, currentLanguage)}
          </Typography>
        </Box>

        <Divider />

        <Box>
          <SectionTitle>
            {translate("migration_section_documents")}
          </SectionTitle>
          <Typography variant="body2">
            {isSeedSpec(spec)
              ? translate("migration_documents_line_seed", {
                  present: state.presentCount ?? 0,
                  expected: spec.expectedDocuments.count,
                  missing: state.affectedCount
                })
              : translate("migration_documents_line", {
                  affected: state.affectedCount,
                  total: state.totalCount
                })}
          </Typography>
        </Box>

        {state.indexChanges.length > 0 && (
          <Box>
            <SectionTitle>
              {translate("migration_section_indexes")}
            </SectionTitle>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    {translate("migration_index_col_action")}
                  </TableCell>
                  <TableCell>
                    {translate("migration_index_col_index")}
                  </TableCell>
                  <TableCell>{translate("migration_index_col_keys")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.indexChanges.map((change) => (
                  <TableRow key={`${change.action}-${change.name}`}>
                    <TableCell>
                      <Chip
                        size="small"
                        label={change.action}
                        color={INDEX_ACTION_COLOR[change.action]}
                      />
                    </TableCell>
                    <TableCell sx={mono}>
                      {change.name}
                      {change.unique ? " (unique)" : ""}
                    </TableCell>
                    <TableCell sx={mono}>{change.keys}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {state.samples.length > 0 && (
          <Box>
            <SectionTitle>
              {translate("migration_section_preview")}
            </SectionTitle>
            <SampleDiff samples={state.samples} hasPreview={!!spec.preview} />
          </Box>
        )}

        <Divider />

        <Box>
          <SectionTitle>{translate("migration_section_history")}</SectionTitle>
          {historyLoading && <CircularProgress size={18} sx={{ mt: 1 }} />}
          {!historyLoading && runs.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {translate("migration_history_empty")}
            </Typography>
          )}
          {runs.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    {translate("migration_history_col_when")}
                  </TableCell>
                  <TableCell>
                    {translate("migration_history_col_result")}
                  </TableCell>
                  <TableCell>
                    {translate("migration_history_col_affected")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runs.map((run) => (
                  <TableRow key={run._id}>
                    <TableCell>
                      {new Date(run.startedAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={translate(`migration_run_status_${run.status}`)}
                        color={RUN_STATUS_COLOR[run.status]}
                      />
                      <Typography variant="caption" display="block">
                        {run.durationMs != null
                          ? formatElapsed(run.durationMs)
                          : "—"}
                      </Typography>
                      {run.error && (
                        <Typography
                          variant="caption"
                          color="error.main"
                          sx={mono}
                        >
                          {run.error}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={mono}>
                      {run.countsBefore ?? "—"} → {run.countsAfter ?? "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>

        {running && lastRun && (
          <Alert severity="info">
            <AlertTitle>
              {translate("migration_run_status_RUNNING")} ·{" "}
              {formatElapsed(now - new Date(lastRun.startedAt).getTime())}
            </AlertTitle>
            {translate("migration_active_run_note")}
            <LinearProgress sx={{ mt: 1 }} />
          </Alert>
        )}

        {lastRun?.status === "INTERRUPTED" && (
          <Alert severity="warning">
            {translate("migration_run_status_help_INTERRUPTED")}
          </Alert>
        )}

        {otherRun && !running && (
          <Alert severity="info">
            {translate("migration_other_run_active", {
              title: pickText(otherRun.spec.title, currentLanguage)
            })}
          </Alert>
        )}

        <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
          <Button
            variant="contained"
            color={spec.risk === "DESTRUCTIVE" ? "error" : "primary"}
            disabled={running || !!otherRun}
            onClick={() => onRun(state)}
          >
            {translate(actionable ? "migration_run" : "migration_run_anyway")}
          </Button>
          <Button
            variant="outlined"
            disabled={reprobing || running}
            onClick={onReprobe}
          >
            {translate(reprobing ? "migration_probing" : "migration_reprobe")}
          </Button>
          <Button onClick={onClose}>{translate("migration_close")}</Button>
        </Stack>

        {!actionable && !state.probeError && (
          <Typography variant="caption" color="text.secondary">
            {translate("migration_nothing_outstanding")}
          </Typography>
        )}
      </Stack>
    </Drawer>
  )
}
